export interface BillingSummaryResponse {
  organization: {
    id: string;
    name: string;
  };
  timeRange: TimeRange;
  timeGrouping: TimeGrouping;
  data: BillingData;
}

type BillingData = {
  transactions: ProjectTransaction[];
  timeSeries: ChartPoint[];
  summary: {
    totalTransactions: number;
    totalSpend: number; // in cents
  };
};

export interface ProjectTransaction {
  id: string;
  createdAt: string;
  description: string;
  amount: number; // in cents
  type: 'debit' | 'credit' | 'discount';
  projectName: string;
  projectId: string;
  billingCode: string;
  billingCode2: string;
}

import { proxy } from 'valtio';
import { getBillingSummary } from '~/api/organizations2';
import type { BillingTimeRange, ChartPoint, TimeGrouping, TimeRange } from '~/common/charts/types';

class BillingSummaryStore {
  loading: boolean = false;
  error: string | null = null;
  chartData: BillingSummaryResponse | null = null;
  allTransactions: ProjectTransaction[] = []; // For keeping the full list
  timeRange: string = 'all'; // Allowing for YYYY-MM format
  groupBy: TimeGrouping = 'month';

  async fetchDashboardData(
    organizationId: string,
    params?: Partial<{ timeRange: BillingTimeRange; groupBy: TimeGrouping }>,
  ) {
    try {
      this.loading = true;
      this.error = null;

      const response = await getBillingSummary(organizationId, {
        timeRange: params?.timeRange || this.timeRange,
        groupBy: params?.groupBy || this.groupBy,
      });

      this.chartData = response;

      if (params?.timeRange === 'all' || (!params?.timeRange && this.timeRange === 'all')) {
        this.allTransactions = response.data.transactions;
      }

      this.timeRange = response.timeRange;
      this.groupBy = response.timeGrouping;
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'An error occurred';
      console.error('Error fetching chart data:', error);
    } finally {
      this.loading = false;
    }
  }

  // Action to update time range and fetch new data
  async updateTimeRange(organizationId: string, timeRange: string) {
    this.timeRange = timeRange;
    await this.fetchDashboardData(organizationId, {
      timeRange,
      groupBy: timeRange === 'all' ? 'month' : 'day',
    });
  }

  // Reset store state
  reset() {
    this.loading = false;
    this.error = null;
    this.chartData = null;
    this.timeRange = 'all';
    this.groupBy = 'month';
  }
}

const billingSummaryStore = proxy(new BillingSummaryStore());

export default billingSummaryStore;
